import React from "react";
import { Button } from "@UI/Components/Button/Button";
import { useDisclosure } from "@UI/hooks/useDisclosure";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";
import { CandidateOfferModal } from "./ServiceOfferModal";
import { IServiceOfferModalWithButtonProps } from "../types";
import { useDataQueryClient } from "@Libs/DataLayer/hooks/useDataQuery";
import { DeclineOfferModal } from "./CandidatesSection/DeclineOffer/DeclineOfferModal";

import { HStack } from "@UI/Components/Stack/Stack";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { Text } from "@UI/Components/Text/Text";
import { Xmark } from "@UI/Components/Icons/Xmark";

import { AlertBox } from "@UI/Components/AlertBox/AlertBox";
import { useToast, ToastProps } from "@UI/hooks/useToast";
import { v4 as uuidv4 } from "uuid";
import useMediaQuery from "@UI/hooks/useMediaQuery";

export const ServiceOfferModalWithButton = (
  props: IServiceOfferModalWithButtonProps
) => {
  const toast = useToast();
  const { isMobile } = useMediaQuery();

  // const { currentUser } = useCurrentUser();

  const {
    isOpen: isCandidateOfferModalOpen,
    onOpen: onCandidateOfferModalOpen,
    onClose: onCandidateOfferModalClose,
  } = useDisclosure();

  const {
    isOpen: isDeclineOfferModalOpen,
    onOpen: onDeclineOfferModalOpen,
    onClose: onDeclineOfferModalClose,
  } = useDisclosure();

  const selectedCandidate = props.candidate;

  const queryClient = useDataQueryClient();

  const handelDeclineOfferModalOpen = () => {
    onCandidateOfferModalClose();
    onDeclineOfferModalOpen();
  };

  const removeCandidateFromList = () => {
    const existingData = queryClient.getQueryData("serviceRequestCandidates");
    Array.isArray(existingData) &&
      queryClient.setQueryData(
        "serviceRequestCandidates",
        existingData.filter(
          (item) => item.conversationId !== selectedCandidate.conversationId
        )
      );
  };

  const showToast = (toastData: Pick<ToastProps, "status" | "description">) => {
    const { status, description } = toastData;
    const toastId = uuidv4();
    toast({
      id: toastId,
      status,
      position: isMobile ? "bottom" : "top-right",
      duration: 2000,
      isClosable: true,
      render: () => (
        <AlertBox
          status={status}
          background="white"
          childrenContainerStyle={{ justifyContent: "start" }}
          marginTop={!isMobile ? "80px" : "initial"}
        >
          <HStack justifyContent="space-between" flexGrow={1}>
            <Text m="0" ml={3} color="gray.700">
              {description}
            </Text>
            <Icon
              as={Xmark}
              color="gray.700"
              cursor="pointer"
              onClick={() => toast.close(toastId)}
            />
          </HStack>
        </AlertBox>
      ),
    });
  };

  // Sanitize the HTML to allow only <span> elements. The rest will be removed automatically.
  // This will remove unsafe code from totalPrice like executable tags <script>
  const sanitizedHTML =
    props.totalPrice && props.totalPrice.replace(/<(?!\/?span\b)[^>]*>/gi, "");

  // const totalExpense =
  //   selectedCandidate?.serviceOffer?.expensesCents +
  //   (!currentUser?.subscribed
  //     ? selectedCandidate?.serviceOffer?.expensesFeesCents
  //     : 0);

  // let totalPrice =
  //   selectedCandidate?.serviceOffer?.rateCents +
  //   (!currentUser?.subscribed
  //     ? selectedCandidate?.serviceOffer?.serviceFeesCents
  //     : 0);

  // if (selectedCandidate?.serviceOffer?.billingUnit === "flat")
  //   totalPrice += totalExpense;

  return (
    <>
      <Button
        w="full"
        data-testid="view-offer-modal"
        // if totalPrice is available, it means we are displaying a SP's price offer with underlined text
        // if not we are displaying see offer button.
        // whoever is reading this, hope you are not annoyed. Trying to save time here.
        {...(props.totalPrice
          ? {
              variant: "unstyled",
              height: "fit-content",
              textAlign: "right",
              fontSize: { base: "1.125rem", md: "1.25rem" },
              mb: "4px",
              sx: { "text-wrap": "nowrap" },
            }
          : { size: props.buttonSize || "lg" })}
        colorScheme="seaGreen"
        data-bs-dismiss="offcanvas" // this will close the legacy profile detail modal or legacy conversation modal
        onClick={onCandidateOfferModalOpen}
      >
        {props.totalPrice ? (
          <>
            {/* <CandidateTransparentPriceDetails
              billingUnit={selectedCandidate?.serviceOffer?.billingUnit}
              totalPrice={totalPrice || 0}
              totalExpense={totalExpense || 0}
              rateCurrency={selectedCandidate?.serviceOffer?.rateCurrency}
              isShortFormat={true}
              isSubscribed={currentUser?.subscribed}
              totalPriceStyle={{
                fontSize: { base: "1.125rem", md: "1.25rem" },
              }}
            /> */}
            <Box
              // used dangerouslySetInnerHTML because totalPrice contains <span>
              dangerouslySetInnerHTML={{ __html: sanitizedHTML }} // since its sanitized, it's safe :)
              fontWeight="700"
            />
          </>
        ) : (
          <FormattedMessage {...messages["serviceRequest.seeOffer"]} />
        )}
      </Button>
      <CandidateOfferModal
        candidate={selectedCandidate}
        isOpen={isCandidateOfferModalOpen}
        onClose={onCandidateOfferModalClose}
        onDeclineOfferModalOpen={() => handelDeclineOfferModalOpen()}
      />
      <DeclineOfferModal
        isOpen={isDeclineOfferModalOpen}
        onClose={onDeclineOfferModalClose}
        conversationId={selectedCandidate?.conversationId}
        serviceProviderName={selectedCandidate?.firstName || ""}
        isOfferMade={!!selectedCandidate?.serviceOffer}
        showToast={showToast}
        removeCandidateFromList={removeCandidateFromList}
      />
    </>
  );
};
